import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

/* ---------- styled components ---------- */
const PageBody = styled.div`
  height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const StudioHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem;
  border-bottom: 1px solid var(--border);
  background: var(--bg-surface);
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const AgentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const BackLink = styled.a`
  color: var(--text-muted);
  font-size: 1.125rem;
  margin-right: 0.25rem;
  cursor: pointer;
`;

const AgentIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: var(--accent-subtle);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
`;

const AgentName = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const AgentType = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const Badge = styled.span<{ $variant?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 500;
  margin-left: 0.5rem;
  background: ${({ $variant }) => $variant === 'green' ? 'var(--success-subtle)' : 'var(--bg-elevated)'};
  color: ${({ $variant }) => $variant === 'green' ? 'var(--success)' : 'var(--text-muted)'};
`;

const StudioActions = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const Btn = styled.button<{ $variant?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: ${({ $variant }) =>
    $variant === 'primary' ? 'none'
    : $variant === 'secondary' ? '1px solid var(--border)'
    : 'none'};
  background: ${({ $variant }) =>
    $variant === 'primary' ? 'var(--accent)'
    : $variant === 'secondary' ? 'var(--bg-elevated)'
    : 'transparent'};
  color: ${({ $variant }) =>
    $variant === 'primary' ? 'white'
    : $variant === 'secondary' ? 'var(--text-primary)'
    : 'var(--text-secondary)'};

  &:hover {
    ${({ $variant }) =>
      $variant === 'primary' ? 'background: var(--accent-hover);'
      : $variant === 'secondary' ? 'background: var(--bg-inset);'
      : 'background: var(--bg-elevated);'}
  }
`;

const StudioLayout = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  overflow: hidden;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

/* -- Left: Editor Panel -- */
const EditorPanel = styled.div`
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--border);
  overflow: hidden;
`;

const PanelHeader = styled.div`
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid var(--border);
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--bg-surface);
`;

const EditorMessages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const EditorMsg = styled.div<{ $role: 'ai' | 'user' }>`
  display: flex;
  gap: 0.75rem;
  ${({ $role }) => $role === 'user' ? 'flex-direction: row-reverse;' : ''}
`;

const MsgAvatar = styled.div<{ $role: 'ai' | 'user' }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6875rem;
  font-weight: 600;
  flex-shrink: 0;
  background: ${({ $role }) => $role === 'ai' ? 'var(--accent-subtle)' : 'var(--bg-elevated)'};
  color: ${({ $role }) => $role === 'ai' ? 'var(--accent)' : 'var(--text-secondary)'};
`;

const MsgContent = styled.div<{ $role: 'ai' | 'user' }>`
  padding: 0.75rem 1rem;
  border-radius: 12px;
  font-size: 0.875rem;
  line-height: 1.6;
  max-width: 85%;
  ${({ $role }) => $role === 'ai'
    ? 'background: var(--bg-surface); border: 1px solid var(--border); color: var(--text-secondary); border-top-left-radius: 4px;'
    : 'background: var(--accent); color: white; border-top-right-radius: 4px;'}
`;

const DiffBlock = styled.div`
  margin-top: 0.75rem;
  background: var(--code-bg);
  border-radius: 8px;
  overflow: hidden;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.8125rem;
`;

const DiffHeader = styled.div`
  padding: 0.5rem 0.75rem;
  background: var(--bg-elevated);
  border-bottom: 1px solid var(--border);
  font-size: 0.75rem;
  color: var(--text-muted);
  font-weight: 500;
`;

const DiffLine = styled.div<{ $type: 'added' | 'removed' }>`
  padding: 0.25rem 0.75rem;
  white-space: pre-wrap;
  background: ${({ $type }) => $type === 'removed' ? 'var(--error-subtle)' : 'var(--success-subtle)'};
  color: ${({ $type }) => $type === 'removed' ? 'var(--error)' : 'var(--success)'};
`;

const DiffActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const EditorInputArea = styled.div`
  padding: 1rem 1.25rem;
  border-top: 1px solid var(--border);
  background: var(--bg-surface);
`;

const EditorInputWrap = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: flex-end;
`;

const EditorInput = styled.textarea`
  flex: 1;
  padding: 0.75rem 1rem;
  background: var(--input-bg);
  border: 1px solid var(--border);
  border-radius: 10px;
  font-family: inherit;
  font-size: 0.875rem;
  color: var(--text-primary);
  outline: none;
  resize: none;
  max-height: 120px;
  line-height: 1.5;
  transition: border-color 0.2s;

  &:focus { border-color: var(--accent); }
  &::placeholder { color: var(--text-muted); }
`;

const QuickBtns = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
`;

/* -- Right: Preview Panel -- */
const PreviewPanel = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--bg-primary);

  @media (max-width: 1024px) {
    display: none;
  }
`;

const PreviewContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
`;

const ConfigSection = styled.div`
  margin-bottom: 1.5rem;
`;

const ConfigSectionTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ConfigCard = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 1rem 1.25rem;
`;

const ConfigRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--border);
  font-size: 0.8125rem;

  &:last-child { border-bottom: none; }
`;

const ConfigKey = styled.span`
  color: var(--text-muted);
  font-weight: 500;
`;

const ConfigValue = styled.span<{ $changed?: boolean }>`
  color: ${({ $changed }) => $changed ? 'var(--success)' : 'var(--text-primary)'};
  font-weight: 500;

  ${({ $changed }) => $changed ? `
    &::after {
      content: '(changed)';
      font-size: 0.6875rem;
      color: var(--success);
      margin-left: 0.375rem;
      font-weight: 400;
    }
  ` : ''}
`;

const PromptPreview = styled.div`
  background: var(--code-bg);
  border-radius: 8px;
  padding: 1rem;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.8125rem;
  line-height: 1.7;
  color: var(--text-secondary);
  max-height: 200px;
  overflow-y: auto;
  white-space: pre-wrap;
`;

/* ---------- component ---------- */
const AgentStudio: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const agentName = id ? id.charAt(0).toUpperCase() + id.slice(1) : 'Agent';

  return (
    <PageBody>
      <StudioHeader>
        <AgentInfo>
          <BackLink onClick={() => navigate('/agents')} title="Back to agents">
            &larr;
          </BackLink>
          <AgentIcon>{agentName[0]}</AgentIcon>
          <div>
            <AgentName>{agentName}</AgentName>
            <AgentType>Management Agent &middot; GPT-4o</AgentType>
          </div>
          <Badge $variant="green">Active</Badge>
        </AgentInfo>
        <StudioActions>
          <Btn $variant="ghost">&#x1F441; Preview</Btn>
          <Btn $variant="secondary">&#x1F9EA; Sandbox</Btn>
          <Btn $variant="primary">&#x1F680; Publish</Btn>
        </StudioActions>
      </StudioHeader>

      <StudioLayout>
        {/* Left: AI Editor */}
        <EditorPanel>
          <PanelHeader>
            <span style={{ color: 'var(--accent)' }}>&#x2728;</span> AI Configuration Editor
          </PanelHeader>

          <EditorMessages>
            <EditorMsg $role="ai">
              <MsgAvatar $role="ai">AI</MsgAvatar>
              <MsgContent $role="ai">
                Hi! I&apos;m your configuration assistant for <strong>{agentName}</strong>. You can describe changes in natural language and I&apos;ll update the agent config. Try things like:
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.25rem', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                  <li>Make {agentName}&apos;s tone more professional</li>
                  <li>Add a new task for checking wait times</li>
                  <li>Change the LLM model to Claude</li>
                  <li>Update the routing logic for reservations</li>
                </ul>
              </MsgContent>
            </EditorMsg>

            <EditorMsg $role="user">
              <MsgAvatar $role="user">SW</MsgAvatar>
              <MsgContent $role="user">
                Make {agentName} more formal and professional in tone. She should address guests as &ldquo;valued guest&rdquo; and avoid casual language.
              </MsgContent>
            </EditorMsg>

            <EditorMsg $role="ai">
              <MsgAvatar $role="ai">AI</MsgAvatar>
              <MsgContent $role="ai">
                I&apos;ll update the system prompt to use a more formal and professional tone. Here&apos;s the change:
                <DiffBlock>
                  <DiffHeader>agents/{id}/prompt.json &mdash; system prompt</DiffHeader>
                  <DiffLine $type="removed">- You are {agentName}, a friendly restaurant assistant.</DiffLine>
                  <DiffLine $type="removed">- Greet guests warmly and use casual language.</DiffLine>
                  <DiffLine $type="added">+ You are {agentName}, a professional restaurant concierge.</DiffLine>
                  <DiffLine $type="added">+ Address all guests as &ldquo;valued guest&rdquo; and maintain a formal, courteous tone.</DiffLine>
                </DiffBlock>
                <DiffActions>
                  <Btn $variant="primary">&#x2713; Apply Change</Btn>
                  <Btn $variant="ghost">&#x2717; Discard</Btn>
                </DiffActions>
              </MsgContent>
            </EditorMsg>

            <EditorMsg $role="user">
              <MsgAvatar $role="user">SW</MsgAvatar>
              <MsgContent $role="user">
                Also increase the temperature to 0.8 so responses feel more natural
              </MsgContent>
            </EditorMsg>

            <EditorMsg $role="ai">
              <MsgAvatar $role="ai">AI</MsgAvatar>
              <MsgContent $role="ai">
                I&apos;ll adjust the temperature setting:
                <DiffBlock>
                  <DiffHeader>agents/{id}/config.json &mdash; settings</DiffHeader>
                  <DiffLine $type="removed">- &quot;temperature&quot;: 0.5</DiffLine>
                  <DiffLine $type="added">+ &quot;temperature&quot;: 0.8</DiffLine>
                </DiffBlock>
                <p style={{ marginTop: '0.5rem', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                  Note: Higher temperature (0.8) will make responses more varied but slightly less deterministic.
                </p>
                <DiffActions>
                  <Btn $variant="primary">&#x2713; Apply Change</Btn>
                  <Btn $variant="ghost">&#x2717; Discard</Btn>
                </DiffActions>
              </MsgContent>
            </EditorMsg>
          </EditorMessages>

          <EditorInputArea>
            <EditorInputWrap>
              <EditorInput rows={1} placeholder='Describe a change... "Add a task for checking wait times"' />
              <Btn $variant="primary" style={{ width: 40, height: 40, borderRadius: 10, padding: 0, justifyContent: 'center' }}>
                &#x2191;
              </Btn>
            </EditorInputWrap>
            <QuickBtns>
              <Btn $variant="ghost" style={{ fontSize: '0.75rem' }}>&#x1F4DD; Edit Prompt</Btn>
              <Btn $variant="ghost" style={{ fontSize: '0.75rem' }}>&#x2699; Change Model</Btn>
              <Btn $variant="ghost" style={{ fontSize: '0.75rem' }}>&#x1F4CB; Add Task</Btn>
              <Btn $variant="ghost" style={{ fontSize: '0.75rem' }}>&#x1F504; Reset Changes</Btn>
            </QuickBtns>
          </EditorInputArea>
        </EditorPanel>

        {/* Right: Config Preview */}
        <PreviewPanel>
          <PanelHeader>
            <span>&#x1F4CB;</span> Agent Configuration
            <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'var(--success)' }}>
              &#x25CF; 2 pending changes
            </span>
          </PanelHeader>

          <PreviewContent>
            <ConfigSection>
              <ConfigSectionTitle>&#x2699; General</ConfigSectionTitle>
              <ConfigCard>
                <ConfigRow><ConfigKey>Name</ConfigKey><ConfigValue>{agentName}</ConfigValue></ConfigRow>
                <ConfigRow><ConfigKey>Type</ConfigKey><ConfigValue>Management</ConfigValue></ConfigRow>
                <ConfigRow><ConfigKey>LLM Provider</ConfigKey><ConfigValue>OpenAI</ConfigValue></ConfigRow>
                <ConfigRow><ConfigKey>Model</ConfigKey><ConfigValue>GPT-4o</ConfigValue></ConfigRow>
                <ConfigRow><ConfigKey>Temperature</ConfigKey><ConfigValue $changed>0.8</ConfigValue></ConfigRow>
                <ConfigRow><ConfigKey>Max Tokens</ConfigKey><ConfigValue>2048</ConfigValue></ConfigRow>
                <ConfigRow><ConfigKey>Voice Output</ConfigKey><ConfigValue>Enabled</ConfigValue></ConfigRow>
              </ConfigCard>
            </ConfigSection>

            <ConfigSection>
              <ConfigSectionTitle>&#x1F4DD; System Prompt</ConfigSectionTitle>
              <PromptPreview>{`You are ${agentName}, a professional restaurant concierge.
Address all guests as "valued guest" and maintain a formal, courteous tone throughout all interactions.

You manage a team of specialized agents:
- ${id}-menu: For menu-related questions
- ${id}-reservations: For table bookings

Route guest queries to the appropriate specialist. If unsure, ask the guest for clarification before delegating.`}</PromptPreview>
            </ConfigSection>

            <ConfigSection>
              <ConfigSectionTitle>&#x1F465; Agent Fleet</ConfigSectionTitle>
              <ConfigCard>
                <ConfigRow>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ width: 24, height: 24, borderRadius: 6, background: 'var(--accent-subtle)', color: 'var(--accent)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.625rem', fontWeight: 600 }}>EM</span>
                    <span><span style={{ fontWeight: 500, color: 'var(--text-primary)', fontSize: '0.8125rem' }}>{id}-menu</span><br /><span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Conversational &middot; Menu queries</span></span>
                  </span>
                </ConfigRow>
                <ConfigRow>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ width: 24, height: 24, borderRadius: 6, background: 'var(--warning-subtle)', color: 'var(--warning)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.625rem', fontWeight: 600 }}>ER</span>
                    <span><span style={{ fontWeight: 500, color: 'var(--text-primary)', fontSize: '0.8125rem' }}>{id}-reservations</span><br /><span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Task &middot; Table bookings</span></span>
                  </span>
                </ConfigRow>
              </ConfigCard>
            </ConfigSection>

            <ConfigSection>
              <ConfigSectionTitle>&#x1F3AF; Capabilities</ConfigSectionTitle>
              <ConfigCard>
                <ConfigRow><ConfigKey>Task Execution</ConfigKey><Badge $variant="green">Enabled</Badge></ConfigRow>
                <ConfigRow><ConfigKey>Agent Delegation</ConfigKey><Badge $variant="green">Enabled</Badge></ConfigRow>
                <ConfigRow><ConfigKey>Agent Spawning</ConfigKey><Badge>Disabled</Badge></ConfigRow>
              </ConfigCard>
            </ConfigSection>

            <ConfigSection>
              <ConfigSectionTitle>&#x1F680; Deploy History</ConfigSectionTitle>
              <ConfigCard>
                <ConfigRow><ConfigKey>Production</ConfigKey><ConfigValue style={{ fontSize: '0.75rem' }}>v1.2 &middot; Feb 7, 2026</ConfigValue></ConfigRow>
                <ConfigRow><ConfigKey>Sandbox</ConfigKey><ConfigValue style={{ fontSize: '0.75rem' }}>v1.3-beta &middot; Feb 9, 2026</ConfigValue></ConfigRow>
              </ConfigCard>
            </ConfigSection>
          </PreviewContent>
        </PreviewPanel>
      </StudioLayout>
    </PageBody>
  );
};

export default AgentStudio;
